
import React, { useEffect, useState, useRef } from 'react';
import { Palette, Layout, Code, Smartphone, MousePointer, Layers } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ServiceCardProps {
  title: string;
  description: string;
  Icon: React.ElementType;
  delay: number;
  isVisible: boolean;
}

const ServiceCard: React.FC<ServiceCardProps> = ({ title, description, Icon, delay, isVisible }) => {
  return (
    <div 
      className={`bg-white p-8 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300 opacity-0 ${
        isVisible ? `animate-fade-in animate-delay-${delay}` : ''
      }`}
    >
      <div className="mb-6 text-developer-accent">
        <Icon size={32} strokeWidth={1.5} />
      </div>
      <h3 className="text-xl font-semibold mb-3 text-developer-dark">{title}</h3>
      <p className="text-developer-neutral">{description}</p>
    </div>
  );
};

const Services = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.1,
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const services = [
    {
      title: t('services.items.0.title'),
      description: t('services.items.0.description'),
      Icon: Layout,
      delay: 100
    },
    {
      title: t('services.items.1.title'),
      description: t('services.items.1.description'),
      Icon: Code,
      delay: 200
    },
    {
      title: t('services.items.2.title'),
      description: t('services.items.2.description'),
      Icon: Palette,
      delay: 300
    },
    {
      title: t('services.items.3.title'),
      description: t('services.items.3.description'),
      Icon: Smartphone,
      delay: 100
    },
    {
      title: t('services.items.4.title'),
      description: t('services.items.4.description'),
      Icon: MousePointer,
      delay: 200
    },
    {
      title: t('services.items.5.title'),
      description: t('services.items.5.description'),
      Icon: Layers,
      delay: 300
    }
  ];

  return (
    <section id="services" className="section bg-developer-light" ref={sectionRef}>
      <div className="container-custom">
        <h2 
          className={`text-3xl md:text-4xl font-bold text-center mb-16 text-developer-dark opacity-0 ${
            isVisible ? 'animate-fade-in' : ''
          }`}
        >
          {t('services.title')}
        </h2>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <ServiceCard
              key={index}
              title={service.title}
              description={service.description}
              Icon={service.Icon}
              delay={service.delay}
              isVisible={isVisible}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
